'use client';

import React from 'react';
import BreederStory from './BreederStory';
import CredentialsSection from './CredentialsSection';
import StatisticsPanel from './StatisticsPanel';
import FacilityGallery from './FacilityGallery';
import BreedingPhilosophy from './BreedingPhilosophy';
import TestimonialHighlights from './TestimonialHighlights';
import HealthTestingProtocol from './HealthTestingProtocol';
import CTASection from './CTASection';

interface BreederStoryData {
  title: string;
  subtitle: string;
  mainImage: string;
  mainImageAlt: string;
  paragraphs: string[];
}

interface Credential {
  id: number;
  title: string;
  organization: string;
  year: string;
  icon: string;
}

interface Statistic {
  id: number;
  value: string;
  label: string;
  icon: string;
}

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  caption: string;
}

interface PhilosophyPoint {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  imageAlt: string;
  rating: number;
  text: string;
  puppyName: string;
}

interface HealthTest {
  id: number;
  name: string;
  description: string;
  icon: string;
}

const AboutUsInteractive = () => {
  const breederStory: BreederStoryData = {
    title: "Reputable Labrador Breeders Since 2008 - English & American Lab Specialists",
    subtitle: "Our passion is raising healthy, AKC-certified Labrador Retriever puppies that become cherished family members",
    mainImage: "https://images.unsplash.com/photo-1557579813-d85a86ebeba2",
    mainImageAlt: "Happy family with premium Yellow Labrador Retriever puppies for sale from reputable English Labrador breeders",
    paragraphs: [
      "Thanks for stopping by Luminex Labradors! We've been raising exceptional Labrador puppies for over 15 years now, and honestly, it's become so much more than just breeding dogs - it's our life's passion. What began with our very first English Lab has blossomed into a full-time commitment to bringing healthy, happy Lab puppies into loving homes across the country.",
      "Since we started back in 2008, we've focused on both English and American Labrador lines, always prioritizing champion bloodlines and wonderful temperaments above everything else. Every puppy we raise gets tons of love, early socialization, and comprehensive health testing. We give each Lab individual attention to make sure they're confident, well-adjusted, and completely ready to thrive in their new home.",
      "As certified Labrador breeders, we believe responsible breeding means combining top-notch genetics with exceptional daily care. When you choose one of our puppies, you're getting a 2-year health guarantee, complete AKC registration, and our support for as long as you need it - we're here for life. Browse our current selection of English and American Lab puppies in Yellow, Black, Chocolate, and Fox Red, and let's find your perfect companion!"
    ]
  };

  const credentials: Credential[] = [
  {
    id: 1,
    title: "AKC Breeder of Merit",
    organization: "American Kennel Club",
    year: "2012",
    icon: "TrophyIcon"
  },
  {
    id: 2,
    title: "Certified Breeder",
    organization: "Labrador Retriever Club",
    year: "2010",
    icon: "AcademicCapIcon"
  },
  {
    id: 3,
    title: "Health Testing Certified",
    organization: "OFA & PennHIP",
    year: "2008",
    icon: "ShieldCheckIcon"
  },
  {
    id: 4,
    title: "Member",
    organization: "National Breed Association",
    year: "2009",
    icon: "StarIcon"
  }];


  const statistics: Statistic[] = [
  {
    id: 1,
    value: "15+",
    label: "Years of Experience",
    icon: "CalendarIcon"
  },
  {
    id: 2,
    value: "500+",
    label: "Happy Families",
    icon: "HeartIcon"
  },
  {
    id: 3,
    value: "100%",
    label: "Health Guarantee Coverage",
    icon: "ShieldCheckIcon"
  },
  {
    id: 4,
    value: "4.9/5",
    label: "Customer Rating",
    icon: "StarIcon"
  }];


  const facilityImages: GalleryImage[] = [
  {
    id: 1,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_157a7f7b0-1768451291179.png",
    alt: "Modern dog kennel facility with spacious indoor runs featuring clean white walls and natural lighting",
    caption: "Spacious Indoor Kennels"
  },
  {
    id: 2,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_17df1eab8-1767781641830.png",
    alt: "Large fenced outdoor play area with green grass and agility equipment for puppies",
    caption: "Outdoor Play Areas"
  },
  {
    id: 3,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1094e450a-1765444262457.png",
    alt: "Cozy puppy nursery room with soft bedding, toys, and warm lighting for newborn Luminex Labradors",
    caption: "Puppy Nursery"
  },
  {
    id: 4,
    src: "https://images.unsplash.com/photo-1732965436921-e22f2b87fab5",
    alt: "Professional veterinary examination room with stainless steel table and medical equipment",
    caption: "Veterinary Care Room"
  },
  {
    id: 5,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0de2bbb-1765444264676.png",
    alt: "Bright socialization room with colorful toys, tunnels, and training equipment for puppy development",
    caption: "Socialization Center"
  },
  {
    id: 6,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_159398f37-1764678848814.png",
    alt: "Clean grooming station with professional bathing tub and grooming tools for puppy care",
    caption: "Grooming Station"
  }];


  const philosophyPoints: PhilosophyPoint[] = [
  {
    id: 1,
    title: "Health Comes First",
    description: "All our parent dogs get thorough health checks including hip and elbow tests, eye exams, and genetic screening. We only breed dogs that are in excellent health.",
    icon: "HeartIcon"
  },
  {
    id: 2,
    title: "Personality Matters",
    description: "We carefully choose parent dogs based on their temperament. This means your puppy will have that friendly, gentle, smart personality that Labs are famous for.",
    icon: "SparklesIcon"
  },
  {
    id: 3,
    title: "Early Socialization",
    description: "From day one, our puppies experience different sounds, textures, and situations. This helps them grow into confident, well-adjusted dogs that are ready for anything.",
    icon: "UserGroupIcon"
  },
  {
    id: 4,
    title: "We're Here for Life",
    description: "When you adopt from us, you're part of our family. We're here to help with training, answer questions, and support you throughout your dog's entire life.",
    icon: "ChatBubbleLeftRightIcon"
  }];


  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Sarah Mitchell",
    location: "Trenton, NJ",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1772a4d6c-1768005110648.png",
    imageAlt: "Smiling woman with shoulder-length brown hair wearing casual blue sweater outdoors",
    rating: 5,
    text: "We absolutely love our yellow Lab, Buddy! The whole experience was so professional and transparent. You can tell they really care about their dogs. Buddy is healthy, well-behaved, and has the perfect personality for our family.",
    puppyName: "Buddy"
  },
  {
    id: 2,
    name: "Michael Chen",
    location: "Trenton, NJ",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3671659-1763299671587.png",
    imageAlt: "Professional Asian man with short black hair in navy business suit smiling confidently",
    rating: 5,
    text: "After looking at tons of breeders, we chose Luminex Labradors and it was the best choice ever. Our chocolate Lab, Luna, came with all her health records, was already well-trained, and fit right into our home. The support we've gotten has been amazing.",
    puppyName: "Luna"
  },
  {
    id: 3,
    name: "Jennifer Rodriguez",
    location: "Trenton, NJ",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e33e7931-1763294360998.png",
    imageAlt: "Hispanic woman with long dark hair wearing red blouse smiling warmly at camera",
    rating: 5,
    text: "They were so transparent and professional—way beyond what we expected. We visited their place, met the parent dogs, and saw how well they care for all their animals. Our black Lab, Max, is everything we hoped for—healthy, happy, and super smart!",
    puppyName: "Max"
  }];


  const healthTests: HealthTest[] = [
  {
    id: 1,
    name: "Hip Dysplasia Screening",
    description: "OFA and PennHIP evaluations",
    icon: "CheckCircleIcon"
  },
  {
    id: 2,
    name: "Elbow Dysplasia Testing",
    description: "OFA certified evaluations",
    icon: "CheckCircleIcon"
  },
  {
    id: 3,
    name: "Eye Examinations",
    description: "CERF certified ophthalmologist",
    icon: "CheckCircleIcon"
  },
  {
    id: 4,
    name: "Heart Clearances",
    description: "Cardiac health screening",
    icon: "CheckCircleIcon"
  },
  {
    id: 5,
    name: "DNA Testing",
    description: "Genetic disease screening",
    icon: "CheckCircleIcon"
  },
  {
    id: 6,
    name: "EIC Testing",
    description: "Exercise-induced collapse screening",
    icon: "CheckCircleIcon"
  }];


  const handleFormSubmit = (data: Record<string, string>) => {
    console.log('Form submitted:', data);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <BreederStory story={breederStory} />
      <StatisticsPanel statistics={statistics} />
      <CredentialsSection credentials={credentials} />
      <BreedingPhilosophy philosophyPoints={philosophyPoints} />
      <FacilityGallery images={facilityImages} />
      <HealthTestingProtocol healthTests={healthTests} />
      <TestimonialHighlights testimonials={testimonials} />
      <CTASection onFormSubmit={handleFormSubmit} />
    </div>);

};

export default AboutUsInteractive;